import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { isEmpty, uniqueId, isFunction } from 'lodash';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { DropDownMenuProvider } from '@contexts/DropDownMenuContext';
import withDropDownMenu from '@hoc/withDropDownMenu';
import getIntlProvider from '@utils/getIntlProvider';
import { stopPropagation } from '@utils/interactionEvents';
import { setApplicationScrollable } from '@stylesheets/helpers';
import EmptyStateBox, {
  EMPTY_STATE_BOX_VARIANT_COMPACT,
} from '@components/2-molecules/EmptyStateBox';
import MenuListItem from '@components/1-atoms/MenuListItem';
import {
  MENU_LIST_ITEM_ENDING_BUTTON_TYPE_RADIO,
  MENU_LIST_ITEM_ENDING_BUTTON_TYPE_CHECKBOX,
  MENU_LIST_ITEM_ENDING_BUTTON_TYPE_SWITCH,
  MENU_LIST_ITEM_ENDING_BUTTON_TYPE_ICON_BUTTON,
} from '@components/1-atoms/MenuListItem/MenuListItem.constants';
import Search from '@components/1-atoms/Search';
import {
  OverlayLayer,
  OuterWrapper,
  Wrapper,
  DraggableWrapper,
  DraggableWrapperClone,
  DndPlaceholder,
  ColumnsContainer,
  Column,
  ColumnHeader,
} from './DropDownMenu.styled';
import {
  DROP_DOWN_MENU_POSITION_TOP_LEFT,
  DROP_DOWN_MENU_POSITION_TOP_CENTER,
  DROP_DOWN_MENU_POSITION_TOP_RIGHT,
  DROP_DOWN_MENU_POSITION_BOTTOM_LEFT,
  DROP_DOWN_MENU_POSITION_BOTTOM_CENTER,
  DROP_DOWN_MENU_POSITION_BOTTOM_RIGHT,
  DROP_DOWN_MENU_POSITION_BOTTOM_LEFT_EDGE,
  DROP_DOWN_MENU_POSITION_BOTTOM_RIGHT_EDGE,
} from '@constants/dropDownMenuConstants';

export const DROP_DOWN_MENU_OVERLAY_ID = 'drop-down-menu-overlay';
export const DROP_DOWN_MENU_ID = 'drop-down-menu';

const intl = getIntlProvider();

const DropDownMenu = forwardRef(
  (
    {
      drag,
      menuId,
      className,
      closeDropDownMenu,
      dataTestId = 'drop-down-menu',
      emptyStateDescription,
      emptyStateIconName,
      emptyStateLinkButtonLabel,
      emptyStateLinkButtonPath,
      emptyStateOnLinkButtonClick,
      emptyStateTitle = intl.formatMessage({
        id: 'dropdownmenu.empty_state.title',
        defaultMessage: 'No items available',
      }),
      menuItems = [],
      menuColumns,
      menuScrollToBottom,
      onFormSelect,
      position = DROP_DOWN_MENU_POSITION_BOTTOM_RIGHT,
      show,
      style,
      withOverlay = true,
      offsetX = 0,
      onMouseOut,
      withSearch = false,
      searchValue = '',
      searchPlaceholder = 'Search',
      searchStyle,
      onSearchChange,
      onSearchKeyDown,
      wrapperWidth = 240,
    },
    ref,
  ) => {
    const wrapperRef = useRef(null);
    const uniqueDropDownIdRef = useRef(uniqueId('toolbar-'));
    const [activeMenuItemIndex, setActiveMenuItemIndex] = useState();
    const hasColumns = menuColumns && menuColumns.length > 0;
    const showEmptyMenuItem = !menuItems.length && !hasColumns;

    const combineRefs = useCallback(
      (element) => {
        wrapperRef.current = element;

        if (ref) {
          ref.current = element;
        }
      },
      [ref],
    );

    const clickOnOverlay = useCallback(() => {
      document.getElementById(DROP_DOWN_MENU_OVERLAY_ID)?.click();
    }, []);

    const handleClick = useCallback(
      (formFieldValue, onClick, shouldCloseOnClick = true) =>
        (e) => {
          onClick?.(e);
          onFormSelect?.(formFieldValue);

          if (shouldCloseOnClick) {
            closeDropDownMenu?.();
            clickOnOverlay();
          }
        },
      [onFormSelect, closeDropDownMenu, clickOnOverlay],
    );

    const onDropDownMenuOpen = useCallback(
      (menuIndex) => () => {
        setActiveMenuItemIndex(menuIndex);
      },
      [],
    );

    const renderListItem = useCallback(
      (
        {
          color,
          description,
          disabled,
          endingButton,
          endingIconName,
          field,
          formFieldValue,
          id,
          isCategoryHeader,
          label,
          leadingIconColor,
          leadingIconName,
          leadingIconUrl,
          navigationItem,
          navigationPath,
          onClick,
          onMouseLeave,
          onMouseEnter,
          onEndingIconButtonClick,
          selected,
          skeleton,
          tooltip,
          withBadge,
          badge,
          badgeContent,
          withDivider,
          dropDownMenuOffsetX = 10,
          dropDownMenuItems,
          dropDownMenuPosition = DROP_DOWN_MENU_POSITION_BOTTOM_LEFT_EDGE,
          dropDownMenuScrolledToBottom,
          showDropDownMenuOnHover = true,
        },
        index,
      ) => {
        const hasNestedDropDownMenu = !!dropDownMenuItems;
        const showNestedDropDownMenuOnClick =
          hasNestedDropDownMenu && !showDropDownMenuOnHover;

        return (
          <ListItem
            key={id || index}
            color={color}
            closeDropDownMenu={
              hasNestedDropDownMenu ? undefined : closeDropDownMenu
            }
            description={description}
            disabled={disabled}
            endingButton={endingButton}
            endingIconName={endingIconName}
            field={field}
            label={label}
            leadingIconName={leadingIconName}
            leadingIconUrl={leadingIconUrl}
            leadingIconColor={leadingIconColor}
            navigationPath={navigationPath}
            navigationItem={navigationItem}
            onClick={stopPropagation(
              handleClick(formFieldValue, onClick, !hasNestedDropDownMenu),
            )}
            onEndingIconButtonClick={stopPropagation(onEndingIconButtonClick)}
            onMouseEnter={onMouseLeave}
            onMouseLeave={onMouseEnter}
            selected={selected || activeMenuItemIndex === index}
            skeleton={skeleton}
            tooltip={tooltip}
            withBadge={withBadge}
            badge={badge}
            badgeContent={badgeContent}
            withDivider={withDivider}
            isCategoryHeader={isCategoryHeader}
            dropDownMenuOverlay={!hasNestedDropDownMenu}
            dropDownMenuOffsetX={dropDownMenuOffsetX}
            dropDownMenuItems={dropDownMenuItems}
            dropDownMenuPosition={dropDownMenuPosition}
            dropDownMenuScrolledToBottom={dropDownMenuScrolledToBottom}
            showDropDownMenuOnHover={
              hasNestedDropDownMenu && showDropDownMenuOnHover
            }
            onDropDownMenuOpen={
              showNestedDropDownMenuOnClick
                ? onDropDownMenuOpen(index)
                : undefined
            }
            onDropDownMenuClose={
              showNestedDropDownMenuOnClick
                ? () => {
                    setActiveMenuItemIndex();
                  }
                : undefined
            }
          />
        );
      },
      [
        closeDropDownMenu,
        handleClick,
        activeMenuItemIndex,
        setActiveMenuItemIndex,
        onDropDownMenuOpen,
      ],
    );

    useEffect(() => {
      if (menuScrollToBottom) {
        wrapperRef.current?.scrollTo(0, wrapperRef.current?.scrollHeight);
      }
    }, [menuScrollToBottom]);

    useEffect(() => {
      if (show) {
        wrapperRef.current?.focus();
      }
    }, [show]);

    useEffect(() => {
      if (show) {
        setApplicationScrollable(false);
      } else {
        setApplicationScrollable(true);
      }

      return () => {
        setApplicationScrollable(true);
      };
    }, [show]);

    const ListItem = useMemo(() => {
      const allItems = hasColumns
        ? menuColumns.flatMap((col) => col.items || [])
        : menuItems;
      const hasNestedMenus = allItems.some((item) => item.dropDownMenuItems);
      // Create the wrapped component lazily inside useMemo to avoid circular dependency
      return hasNestedMenus ? withDropDownMenu(MenuListItem) : MenuListItem;
    }, [menuItems, menuColumns, hasColumns]);

    return (
      <DropDownMenuProvider>
        {withOverlay && show && (
          <OverlayLayer
            id={DROP_DOWN_MENU_OVERLAY_ID}
            onClick={stopPropagation(closeDropDownMenu)}
          />
        )}

        <OuterWrapper
          $show={show}
          $offsetX={offsetX}
          $position={position}
          data-menu-id={menuId}
          $fixedPosition={style?.position === 'fixed'}
          style={style}
          onMouseOut={onMouseOut}
          $wrapperWidth={wrapperWidth}
          $hasColumns={hasColumns}
        >
          <Wrapper
            className={className}
            data-testid={dataTestId}
            id={DROP_DOWN_MENU_ID}
            ref={combineRefs}
            tabIndex={0}
            $hasColumns={hasColumns}
          >
            {withSearch && (
              <Search
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={onSearchChange}
                onKeyDown={onSearchKeyDown}
                dataTestId={`${dataTestId}__search`}
                style={{
                  marginBottom: '8px',
                  ...searchStyle,
                }}
              />
            )}

            {showEmptyMenuItem && (
              <EmptyStateBox
                variant={EMPTY_STATE_BOX_VARIANT_COMPACT}
                iconName={emptyStateIconName}
                title={emptyStateTitle}
                description={emptyStateDescription}
                linkButtonLabel={emptyStateLinkButtonLabel}
                linkButtonPath={emptyStateLinkButtonPath}
                openLinkInNewTab
                onLinkButtonClick={emptyStateOnLinkButtonClick}
              />
            )}

            {hasColumns && (
              <ColumnsContainer>
                {menuColumns.map((column, columnIndex) => (
                  <Column key={column.id || `column-${columnIndex}`}>
                    {column.header && (
                      <ColumnHeader>{column.header}</ColumnHeader>
                    )}
                    {column.items?.map((item, index) =>
                      renderListItem(item, index),
                    )}
                  </Column>
                ))}
              </ColumnsContainer>
            )}

            {!hasColumns &&
              isEmpty(drag) &&
              menuItems.map((item, index) => renderListItem(item, index))}

            {!hasColumns && !isEmpty(drag) && (
              <Droppable
                droppableId={`${drag?.droppableId}-${uniqueDropDownIdRef.current}`}
                isDropDisabled
              >
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {menuItems.map((menuItemProps, index) => {
                      return (
                        <Draggable
                          key={`${drag?.droppableId}-${uniqueDropDownIdRef.current}-toolbar-${menuItemProps?.id}`}
                          index={index}
                          draggableId={`${
                            menuItemProps?.draggableId ||
                            `draggableId:{${menuItemProps?.id}}`
                          } uniqueComponentId:{${uniqueDropDownIdRef.current}}`}
                          isDragDisabled={drag?.isDragDisabled}
                          clone
                        >
                          {(provided, snapshot) => {
                            const isDraggingOverTargetDroppable =
                              snapshot.draggingOver === drag?.droppableId;
                            const wrapperElementDimensions =
                              wrapperRef.current?.getBoundingClientRect();

                            return (
                              <>
                                <DraggableWrapper
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  tabIndex={-1}
                                  ref={provided.innerRef}
                                  draggingOverDroppable={
                                    isDraggingOverTargetDroppable
                                  }
                                  dragging={snapshot.isDragging}
                                  style={{
                                    ...provided.draggableProps.style,
                                    ...(snapshot.isDragging
                                      ? {
                                          width: 'unset',
                                          height: 'unset',
                                        }
                                      : {}),
                                    ...(snapshot.isDropAnimating
                                      ? {
                                          opacity: 0,
                                          transform: 'translate(0)',
                                          transition:
                                            'transform 0.01s cubic-bezier(.2,1,.1,1)',
                                        }
                                      : {
                                          top:
                                            provided.draggableProps.style.top -
                                            wrapperElementDimensions?.top,
                                          left:
                                            provided.draggableProps.style.left -
                                            wrapperElementDimensions?.left,
                                          transform:
                                            provided.draggableProps.style
                                              .transform,
                                          transition:
                                            provided.draggableProps.style
                                              .transition,
                                        }),
                                  }}
                                >
                                  {/* until start dragging or when draggable element is not passed */}
                                  {(!snapshot.isDragging ||
                                    (snapshot.isDragging &&
                                      !drag?.draggableElement)) &&
                                    renderListItem(menuItemProps, index)}

                                  {/* show draggable element when is dragging */}
                                  {snapshot.isDragging &&
                                    drag?.draggableElement &&
                                    (isFunction(drag?.draggableElement)
                                      ? drag?.draggableElement(
                                          menuItemProps?.dragData,
                                          isDraggingOverTargetDroppable,
                                        )
                                      : drag?.draggableElement)}
                                </DraggableWrapper>

                                {/* placeholder to keep list still and the dragging element is not moved from the list on drag */}
                                {snapshot.isDragging && (
                                  <DraggableWrapperClone>
                                    {renderListItem(menuItemProps, index)}
                                  </DraggableWrapperClone>
                                )}
                              </>
                            );
                          }}
                        </Draggable>
                      );
                    })}

                    <DndPlaceholder>{provided.placeholder}</DndPlaceholder>
                  </div>
                )}
              </Droppable>
            )}
          </Wrapper>
        </OuterWrapper>
      </DropDownMenuProvider>
    );
  },
);

DropDownMenu.propTypes = {
  drag: PropTypes.shape({
    droppableId: PropTypes.string,
    isDragDisabled: PropTypes.bool,
    draggableElement: PropTypes.func,
  }),
  className: PropTypes.string,
  closeDropDownMenu: PropTypes.func,
  dataTestId: PropTypes.string,
  emptyStateDescription: PropTypes.string,
  emptyStateIconName: PropTypes.string,
  emptyStateLinkButtonLabel: PropTypes.string,
  emptyStateLinkButtonPath: PropTypes.string,
  emptyStateOnLinkButtonClick: PropTypes.func,
  emptyStateTitle: PropTypes.string,
  show: PropTypes.bool,
  position: PropTypes.oneOf([
    DROP_DOWN_MENU_POSITION_TOP_LEFT,
    DROP_DOWN_MENU_POSITION_TOP_CENTER,
    DROP_DOWN_MENU_POSITION_TOP_RIGHT,
    DROP_DOWN_MENU_POSITION_BOTTOM_LEFT,
    DROP_DOWN_MENU_POSITION_BOTTOM_CENTER,
    DROP_DOWN_MENU_POSITION_BOTTOM_RIGHT,
    DROP_DOWN_MENU_POSITION_BOTTOM_LEFT_EDGE,
    DROP_DOWN_MENU_POSITION_BOTTOM_RIGHT_EDGE,
  ]),
  menuId: PropTypes.string,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      formFieldValue: PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }),
      color: PropTypes.string,
      description: PropTypes.string,
      endingIconName: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      leadingIconName: PropTypes.string,
      leadingIconColor: PropTypes.string,
      endingButton: PropTypes.shape({
        // Switch, Radio, Checkbox props
        name: PropTypes.string,
        type: PropTypes.oneOf([
          MENU_LIST_ITEM_ENDING_BUTTON_TYPE_SWITCH,
          MENU_LIST_ITEM_ENDING_BUTTON_TYPE_RADIO,
          MENU_LIST_ITEM_ENDING_BUTTON_TYPE_CHECKBOX,
          MENU_LIST_ITEM_ENDING_BUTTON_TYPE_ICON_BUTTON,
        ]).isRequired,
        onChange: PropTypes.func,
        value: PropTypes.string,
        checked: PropTypes.bool,
        enabled: PropTypes.bool,

        // Icon Button props
        disabled: PropTypes.bool,
        iconName: PropTypes.string,
        loading: PropTypes.bool,
        onClick: PropTypes.func,
        selected: PropTypes.bool,
        title: PropTypes.string,
        variant: PropTypes.string,
      }),
      onClick: PropTypes.func,
      onEndingIconButtonClick: PropTypes.func,
      selected: PropTypes.bool,
      skeleton: PropTypes.bool,
      tooltip: PropTypes.object,
      disabled: PropTypes.bool,
      navigationPath: PropTypes.string,
      withBadge: PropTypes.bool,
      withDivider: PropTypes.bool,
    }),
  ),
  menuColumns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      header: PropTypes.string,
      items: PropTypes.array,
    }),
  ),
  menuScrollToBottom: PropTypes.bool,
  onFormSelect: PropTypes.func,
  style: PropTypes.shape({
    position: PropTypes.string,
    top: PropTypes.number,
    left: PropTypes.number,
  }),
  withOverlay: PropTypes.bool,
  offsetX: PropTypes.number,
  onMouseOut: PropTypes.func,
  withSearch: PropTypes.bool,
  searchValue: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  searchStyle: PropTypes.object,
  onSearchChange: PropTypes.func,
  onSearchKeyDown: PropTypes.func,
  wrapperWidth: PropTypes.number,
};

DropDownMenu.displayName = 'DropDownMenu';

export default DropDownMenu;
